// Phase 8 port of rewise-ui's Phase 7 ExamPrepOrchestrationPage.tsx — the
// "what are you getting ready for" entry point. Per
// rewise_app_design_reference.md §8: an orchestrated preparation journey, not
// a 9-card menu — the student states intent and Branon assembles the
// relevant capabilities underneath.
//
// Routed at /exam-prep-plan (NOT /exam-prep) and named ExamPrepOrchestration-
// Page.tsx / examPrepOrchestrationService.ts specifically to avoid colliding
// with this app's existing, unrelated, confirmed-dead static content-browser
// feature (ExamPrepHome/Subjects/Chapters/Modules + examPrepService.ts),
// which is left completely untouched per product decision.
//
// One flagged deviation from the literal mock (same one rewise-ui's version
// made): a subject_select step sits between intent and "Your preparation" --
// Coverage's "X of Y concepts touched" is inherently subject-scoped, so this
// step is the smallest change that makes Coverage computable at all.

import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { fetchConceptsPlus1 } from "../services/conceptPlus1Service";
import { getStudentConceptState, type StudentConceptState } from "../services/masteryService";
import type { ConceptPlus1 } from "../types";
import { getExamsForStudent } from "../config/regionConfig";
import {
  getExamPrepChecklist,
  toggleExamPrepChecklistItem,
  generateExamPrepContent,
  gradeBandFor,
  type ExamPrepIntent,
  type ChecklistItemKey,
  type ChecklistItem,
  type QuickRevisionContent,
  type CheatSheetContent,
} from "../services/examPrepOrchestrationService";
import TTSButton from "../components/TTSButton";
import PomodoroTimer from "../components/PomodoroTimer";
import { ChevronDown, ChevronRight, CheckCircle2, Circle } from "lucide-react";

type Phase = "intent_select" | "subject_select" | "preparation";

const INTENT_LABELS: Record<ExamPrepIntent, { title: string; blurb: string }> = {
  test_tomorrow: { title: "Test tomorrow", blurb: "Quick, focused prep for something coming up fast." },
  term_exam: { title: "Term exam", blurb: "A broader review across what you've covered this term." },
  board_exam: { title: "Board exam", blurb: "The full works — real exam practice and simulation." },
};

const INTENT_CONFIG: Record<ExamPrepIntent, {
  weakestN: number;
  autoExpandRevision: boolean;
  emphasizeSimulation: boolean;
  checklistItems: ChecklistItemKey[];
}> = {
  test_tomorrow: { weakestN: 3, autoExpandRevision: true, emphasizeSimulation: false, checklistItems: ["practice_sheet", "quick_revision"] },
  term_exam: { weakestN: 5, autoExpandRevision: false, emphasizeSimulation: false, checklistItems: ["practice_sheet", "real_exam_questions", "quick_revision", "simulation"] },
  board_exam: { weakestN: 7, autoExpandRevision: false, emphasizeSimulation: true, checklistItems: ["practice_sheet", "real_exam_questions", "quick_revision", "simulation"] },
};

// Minimal shape used for weakest-picking / Coverage math -- StudentConceptState
// (real mastery history) satisfies this structurally; concepts the student
// has never touched at all (zero mastery_events, so absent from
// get_student_concept_state's result) get synthesized into the same shape
// with concept_state='not_started' -- see the subjectStates memo below.
interface QueueRow {
  concept_id: number;
  concept_name: string;
  concept_state: StudentConceptState["concept_state"];
  mastery_score: number;
}

function pickWeakest(states: QueueRow[], n: number): QueueRow[] {
  const sorted = [...states].sort((a, b) => {
    const aStrong = a.concept_state === "strong" ? 1 : 0;
    const bStrong = b.concept_state === "strong" ? 1 : 0;
    if (aStrong !== bStrong) return aStrong - bStrong;
    return a.mastery_score - b.mastery_score;
  });
  return sorted.slice(0, n);
}

type ContentState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; bullets?: string[]; items?: { label: string; value: string }[] }
  | { status: "error" };

export default function ExamPrepOrchestrationPage() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("intent_select");
  const [intent, setIntent] = useState<ExamPrepIntent | null>(null);
  const [subject, setSubject] = useState<string | null>(null);

  const [conceptStates, setConceptStates] = useState<StudentConceptState[]>([]);
  const [allConcepts, setAllConcepts] = useState<ConceptPlus1[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  const [revisionOpen, setRevisionOpen] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [revisionByConceptId, setRevisionByConceptId] = useState<Record<number, ContentState>>({});
  const [cheatSheetByConceptId, setCheatSheetByConceptId] = useState<Record<number, ContentState>>({});

  useEffect(() => {
    if (!session?.student_id) return;
    let mounted = true;
    getStudentConceptState(session.student_id).then((states) => {
      if (!mounted) return;
      setConceptStates(states.filter((s) => s.subject_name && s.concept_name));
    });
    return () => {
      mounted = false;
    };
  }, [session?.student_id]);

  // Full concepts_plus1 catalog -- used both for Coverage's denominator
  // (counted per subject_name) and for offering every subject the board has
  // in subject_select, not just ones the student has already touched.
  useEffect(() => {
    let mounted = true;
    fetchConceptsPlus1().then((concepts) => {
      if (!mounted) return;
      setAllConcepts(concepts);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const subjectConceptCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of allConcepts) {
      if (!c.subject_name) continue;
      counts[c.subject_name] = (counts[c.subject_name] ?? 0) + 1;
    }
    return counts;
  }, [allConcepts]);

  const subjectNames = useMemo(() => {
    const set = new Set<string>();
    allConcepts.forEach((c) => {
      if (c.subject_name) set.add(c.subject_name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allConcepts]);

  // Combines real mastery history with the rest of the subject's catalog --
  // concepts the student has never touched at all still need to show up here
  // so a never-studied subject isn't a dead end for Practice/Coverage/etc.
  const subjectStates = useMemo((): QueueRow[] => {
    if (!subject) return [];
    const touched: QueueRow[] = conceptStates.filter((s) => s.subject_name === subject);
    const touchedIds = new Set(touched.map((s) => s.concept_id));
    const untouched: QueueRow[] = allConcepts
      .filter((c) => c.subject_name === subject && !touchedIds.has(c.id))
      .map((c) => ({
        concept_id: c.id,
        concept_name: c.name,
        concept_state: "not_started" as const,
        mastery_score: 0,
      }));
    return [...touched, ...untouched];
  }, [conceptStates, allConcepts, subject]);

  const config = intent ? INTENT_CONFIG[intent] : null;
  const weakest = useMemo(() => {
    if (!config) return [];
    return pickWeakest(subjectStates, config.weakestN);
  }, [subjectStates, config]);

  useEffect(() => {
    if (phase !== "preparation" || !session?.student_id || !subject || !intent) return;
    getExamPrepChecklist(session.student_id, subject, intent).then(setChecklist);
  }, [phase, session?.student_id, subject, intent]);

  useEffect(() => {
    if (config?.autoExpandRevision) setRevisionOpen(true);
  }, [config?.autoExpandRevision]);

  function isChecked(key: ChecklistItemKey): boolean {
    return checklist.find((c) => c.item_key === key)?.completed ?? false;
  }

  function markAttempted(key: ChecklistItemKey) {
    if (!session?.student_id || !subject || !intent) return;
    setChecklist((prev) => {
      const next = prev.filter((c) => c.item_key !== key);
      next.push({ item_key: key, completed: true, completed_at: new Date().toISOString() });
      return next;
    });
    toggleExamPrepChecklistItem(session.student_id, subject, intent, key, true);
  }

  function handleToggleChecklist(key: ChecklistItemKey) {
    if (!session?.student_id || !subject || !intent) return;
    const next = !isChecked(key);
    setChecklist((prev) => {
      const rest = prev.filter((c) => c.item_key !== key);
      rest.push({ item_key: key, completed: next, completed_at: next ? new Date().toISOString() : null });
      return rest;
    });
    toggleExamPrepChecklistItem(session.student_id, subject, intent, key, next);
  }

  function handlePickIntent(chosen: ExamPrepIntent) {
    setIntent(chosen);
    setPhase("subject_select");
  }

  function handlePickSubject(chosen: string) {
    setSubject(chosen);
    setPhase("preparation");
  }

  function handleContinuePractice() {
    const weakestOne = pickWeakest(subjectStates, 1)[0];
    if (!weakestOne) return;
    markAttempted("practice_sheet");
    navigate(`/concept/${weakestOne.concept_id}?practice=1`);
  }

  function resolveExamNavTarget(): string {
    if (!session || !subject) return "/practice-test";
    const matches = getExamsForStudent(session.grade_level, session.board);
    if (matches.length === 1) {
      return `/practice-test/${encodeURIComponent(matches[0].name)}?subject=${encodeURIComponent(subject)}`;
    }
    return "/practice-test";
  }

  function handleRealExamQuestions() {
    markAttempted("real_exam_questions");
    navigate(resolveExamNavTarget());
  }

  function handleStartSimulation() {
    markAttempted("simulation");
    navigate(resolveExamNavTarget());
  }

  async function loadContentFor(
    contentType: "quick_revision" | "cheat_sheet",
    setter: Dispatch<SetStateAction<Record<number, ContentState>>>
  ) {
    if (!session || !subject) return;
    const gradeBand = gradeBandFor(session.grade_level);
    await Promise.all(
      weakest.map(async (s) => {
        setter((prev) => ({ ...prev, [s.concept_id]: { status: "loading" } }));
        const result = await generateExamPrepContent({
          conceptId: s.concept_id,
          conceptName: s.concept_name,
          subject,
          gradeBand,
          contentType,
          conceptTable: "concepts_plus1",
        });
        if (result.error || !result.content_json) {
          setter((prev) => ({ ...prev, [s.concept_id]: { status: "error" } }));
          return;
        }
        const content = result.content_json as QuickRevisionContent & CheatSheetContent;
        setter((prev) => ({ ...prev, [s.concept_id]: { status: "ready", bullets: content.bullets, items: content.items } }));
      })
    );
  }

  function handleToggleRevision() {
    const next = !revisionOpen;
    setRevisionOpen(next);
    if (next) {
      markAttempted("quick_revision");
      if (Object.keys(revisionByConceptId).length === 0) loadContentFor("quick_revision", setRevisionByConceptId);
    }
  }

  function handleToggleCheatSheet() {
    const next = !cheatSheetOpen;
    setCheatSheetOpen(next);
    if (next && Object.keys(cheatSheetByConceptId).length === 0) {
      loadContentFor("cheat_sheet", setCheatSheetByConceptId);
    }
  }

  const coverageTouched = subjectStates.filter((s) => s.concept_state !== "not_started").length;
  const coverageTotal = subject ? subjectConceptCounts[subject] ?? subjectStates.length : 0;

  if (isLoading || !session) {
    return <div className="max-w-2xl mx-auto p-6 pt-24 text-gray-400">Loading…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 pb-6 pt-24">
      <h1 className="text-2xl font-bold text-ocean-deep mb-1">Exam Prep</h1>
      <p className="text-gray-500 mb-6">Tell us what you're getting ready for — we'll pull together what matters.</p>

      {phase === "intent_select" && (
        <div className="grid gap-3">
          {(Object.keys(INTENT_LABELS) as ExamPrepIntent[]).map((key) => (
            <button
              key={key}
              onClick={() => handlePickIntent(key)}
              className="text-left p-4 rounded-lg border border-gray-200 hover:border-coral-warm hover:bg-orange-50 transition"
            >
              <div className="font-semibold text-ocean-deep">{INTENT_LABELS[key].title}</div>
              <div className="text-sm text-gray-500">{INTENT_LABELS[key].blurb}</div>
            </button>
          ))}
        </div>
      )}

      {phase === "subject_select" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="exam-prep-subject-select">
            Subject
          </label>
          <select
            id="exam-prep-subject-select"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) handlePickSubject(e.target.value);
            }}
            className="w-full p-3 rounded-lg border border-gray-300 text-gray-900 bg-white"
          >
            <option value="" disabled>
              Choose a subject…
            </option>
            {subjectNames.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {subjectNames.length === 0 && (
            <p className="text-gray-400 text-sm mt-3">No subjects found yet.</p>
          )}
        </div>
      )}

      {phase === "preparation" && subject && intent && (
        <div>
          <p className="text-sm text-gray-400 mb-4">{INTENT_LABELS[intent].title} · {subject}</p>

          <div className="grid gap-3 mb-6">
            {/* Coverage */}
            <div className="glass-card rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Coverage</p>
              <p className="text-sm text-gray-800">
                {coverageTouched} of {coverageTotal || coverageTouched} concepts touched in {subject}
              </p>
            </div>

            {/* Practice */}
            <button
              onClick={handleContinuePractice}
              className="glass-card rounded-xl p-4 shadow-sm text-left hover:shadow-md transition-all flex items-center justify-between gap-3"
            >
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Practice</p>
                <p className="text-sm text-gray-800">Continue Practice Sheet</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            {/* Real Exam Questions */}
            <button
              onClick={handleRealExamQuestions}
              className={`glass-card rounded-xl p-4 shadow-sm text-left hover:shadow-md transition-all flex items-center justify-between gap-3 ${
                config?.emphasizeSimulation ? "border-2 border-coral-warm" : ""
              }`}
            >
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Real Exam Questions</p>
                <p className="text-sm text-gray-800">Try questions asked in previous exams</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            {/* Quick Revision */}
            <div className="glass-card rounded-xl shadow-sm overflow-hidden">
              <button onClick={handleToggleRevision} className="w-full p-4 text-left flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Quick Revision</p>
                  <p className="text-sm text-gray-800">Review key ideas</p>
                </div>
                {revisionOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {revisionOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {weakest.map((s) => {
                    const state = revisionByConceptId[s.concept_id];
                    return (
                      <div key={s.concept_id} className="border-t border-gray-100 pt-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900">{s.concept_name}</p>
                          {state?.status === "ready" && state.bullets && (
                            <TTSButton text={state.bullets.join(". ")} />
                          )}
                        </div>
                        {state?.status === "loading" && <p className="text-xs text-gray-400">Getting the key ideas…</p>}
                        {state?.status === "error" && <p className="text-xs text-red-500">Couldn't load this one — try again later.</p>}
                        {state?.status === "ready" && state.bullets && (
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                            {state.bullets.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Start Simulation */}
            <button
              onClick={handleStartSimulation}
              className={`rounded-xl p-4 shadow-md text-left transition-all flex items-center justify-between gap-3 text-white ${
                config?.emphasizeSimulation
                  ? "bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800"
                  : "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              }`}
            >
              <p className="font-bold text-sm">Start Simulation</p>
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            </button>
          </div>

          {/* Secondary utility panels — visually smaller, kept below the core journey */}
          <div className="grid gap-3">
            {/* Cheat Sheet */}
            <div className="glass-card rounded-xl shadow-sm overflow-hidden">
              <button onClick={handleToggleCheatSheet} className="w-full p-3 text-left flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-700">Cheat Sheet</p>
                {cheatSheetOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {cheatSheetOpen && (
                <div className="px-3 pb-3 space-y-3">
                  {weakest.map((s) => {
                    const state = cheatSheetByConceptId[s.concept_id];
                    return (
                      <div key={s.concept_id} className="border-t border-gray-100 pt-2">
                        <p className="text-xs font-semibold text-gray-600 mb-1">{s.concept_name}</p>
                        {state?.status === "loading" && <p className="text-xs text-gray-400">Loading…</p>}
                        {state?.status === "error" && <p className="text-xs text-red-500">Couldn't load this one.</p>}
                        {state?.status === "ready" && state.items && (
                          <div className="space-y-1">
                            {state.items.map((item, i) => (
                              <p key={i} className="text-xs text-gray-700"><span className="font-medium">{item.label}:</span> {item.value}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Checklist */}
            <div className="glass-card rounded-xl shadow-sm p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Checklist</p>
              <div className="space-y-1.5">
                {config?.checklistItems.map((key) => (
                  <button
                    key={key}
                    onClick={() => handleToggleChecklist(key)}
                    className="w-full flex items-center gap-2 text-left text-sm text-gray-700"
                  >
                    {isChecked(key) ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    )}
                    {CHECKLIST_LABELS[key]}
                  </button>
                ))}
              </div>
              <Link to="/practice-history" className="block mt-3 pt-3 border-t border-gray-100 text-sm text-coral-warm hover:underline">
                Practice History →
              </Link>
            </div>

            {/* Timer */}
            <PomodoroTimer />
          </div>

          <Link to="/dashboard" className="inline-block mt-6 text-sm text-coral-warm hover:underline">
            Back to Dashboard
          </Link>
        </div>
      )}
    </div>
  );
}

const CHECKLIST_LABELS: Record<ChecklistItemKey, string> = {
  practice_sheet: "Practice Sheet",
  real_exam_questions: "Real Exam Questions",
  quick_revision: "Quick Revision",
  simulation: "Simulation",
};
